import React, { useState, useEffect, useRef } from 'react';
// import LazyLoad from "react-lazyload";
import cx from 'classnames';
import Slider from 'react-slick';
import PropTypes from 'prop-types';
import breakpoints from 'utils/breakpoints';
import styled from 'styled-components';
import tw from 'twin.macro';
import { IconButton } from 'components/components/atoms';
import useBreakpoint from 'utils/hooks/useBreakpoint';
import getEmbedUrl from 'utils/getEmbedUrl';
import getVideoType from 'utils/getVideoType';
import pauseVideos from 'utils/pauseVideos';
import {
  videoTranscriptFileUrl,
  videoTranscriptFileExtension,
} from 'utils/videoTranscriptFileUrl';
import { getScrFromIframe } from 'utils/getScrFromIframe';
import { AspectRatio } from 'react-aspect-ratio';

const Container = styled.div`
  ${({ fullWidth }) => fullWidth && 'max-height: 22.25rem;'}
  @media ${breakpoints.md} {
    ${({ fullWidth }) => fullWidth && 'max-height: 40.6rem;'}
  }
`;
const IframeContainer = styled.div`
  & iframe {
    ${tw`w-full`}
  }
`;
const Iframe = styled.iframe`
  @media ${breakpoints.md} {
    height: 24.375rem;
  }
  @media ${breakpoints.lg} {
    height: 33.125rem;
  }
  @media ${breakpoints.xl} {
    height: 40.6rem;
  }
`;
const DescriptionSpan = styled.span`
  margin-left: 5%;
  margin-right: 5%;
  @media ${breakpoints.md} {
    margin-left: 15%;
    margin-right: 15%;
  }
  @media ${breakpoints.lg} {
    margin-left: 15%;
    margin-right: 15%;
  }
  @media ${breakpoints.xl} {
    margin-left: 20%;
    margin-right: 20%;
  }
`;

const QMIFlagMessageContainer = styled.div`
  border-top-right-radius: 1.813rem;
  border-bottom-right-radius: 1.813rem;
  width: fit-content;
  border-top-width: 0.125rem;
  border-bottom-width: 0.125rem;
  border-right-width: 0.125rem;
  height: 2.25rem;
`;

const CustomArrowContainer = styled.div`
  &:before {
    display: none;
  }
  z-index: 2;
`;
const ImgContainer = styled.img`
  display: block;
  margin-left: auto;
  margin-right: auto;

  ${({ isCondo }) => isCondo && 'height: 20rem;'}
  @media ${breakpoints.md} {
    ${({ isCondo }) => isCondo && 'height: 16.375rem;'}
  }
  @media ${breakpoints.lg} {
    ${({ isCondo }) => isCondo && 'height: 24.125rem;'}
  }
  @media ${breakpoints.xl} {
    ${({ isCondo }) => isCondo && 'height: 33.6rem;'}
  }
`;
const InternalVideo = styled.video`
  min-height: 22.25rem;
  min-width: 100%;
  @media ${breakpoints.md} {
    min-height: 30.5rem;
    max-height: 40.6rem;
  }
`;
const SlideContainer = styled.div`
  ${({ fullWidth }) => fullWidth && 'min-height: 22.25rem;'}
  @media ${breakpoints.md} {
    ${({ fullWidth }) => fullWidth && 'max-height: 40.6rem;'}
  }
`;

const CustomNextArrow = (props) => {
  const { className, style, onClick, isMobile, noArrows } = props;
  return (
    <CustomArrowContainer
      className={cx('mr-5 md:mr-10', className)}
      style={{ ...style }}
      onClick={onClick}
    >
      <IconButton
        className={cx(
          'absolute right-0 bottom-0 top-0 m-auto z-20 transition-opacity duration-200 flex mr-5 w-10 h-10 md:w-16 md:h-16 md:mr-8',
          { hidden: noArrows },
        )}
        icon='arrow-right'
        idIcon={'Gallery'}
        color='white'
        circled
        shadowed
        hoverState
        buttonLabel='Next slide of Gallery'
        small={isMobile}
        title={'Right arrow icon'}
      />
    </CustomArrowContainer>
  );
};

const CustomPrevArrow = (props) => {
  const { className, style, onClick, isMobile, noArrows } = props;
  return (
    <CustomArrowContainer
      className={cx('ml-5 md:ml-10', className)}
      style={{ ...style }}
      onClick={onClick}
    >
      <IconButton
        className={cx(
          'absolute left-0 bottom-0 top-0 m-auto z-20 transition-opacity duration-200 flex ml-5 sm:w-10 sm:h-10 md:w-16 md:h-16 md:ml-8',
          { hidden: noArrows },
        )}
        icon='arrow-left'
        idIcon={'Gallery'}
        color='white'
        circled
        shadowed
        hoverState
        buttonLabel='Previous slide of Gallery'
        small={isMobile}
        title={'Left arrow icon'}
      />
    </CustomArrowContainer>
  );
};

const isPlanType = (item) =>
  ['floorplan', 'planoption', 'keyplan'].includes(item.type);

const ImageCarousel = ({
  fullWidth = false,
  className,
  carousel,
  onChange = () => {},
  withTopBar,
  rounded = false,
  counterRight = false,
  noArrows = false,
  isContainerRounded = true,
  plansStyle = false,
  reset = false,
  descriptionColor = '',
  onChangeReset = () => {},
  alwaysShowLen = false,
  communityGallery,
  backgroundColor = 'black',
  isMultiblockComponent = false,
  isRightAligned = false,
  hasZoomIcons = false,
  showNewSchemaColor = false,
  messageFlag = '',
  mobileAspectRatio = '375/310',
  desktopAspectRatio = '1200/650',
}) => {
  const breakpoint = useBreakpoint(
    typeof window !== 'undefined' && window.innerWidth,
  );
  const isMobile = ['sm', 'xs'].includes(breakpoint);

  const [position, setPosition] = useState(0);
  const [description, setDescription] = useState('');

  const galleryContainerRef = useRef();
  const [activeIndex, setActiveIndex] = useState(-1);
  const itemRefs = useRef([]);
  const containerRef = useRef(null);
  const [elementsHeight, setElementsHeight] = useState();

  const len = carousel.length;

  useEffect(() => {
    if (containerRef.current && containerRef.current.firstElementChild) {
      const width = containerRef.current.firstElementChild.clientWidth;
      //console.log('Element width:', width);
      const ratio = isMobile ? mobileAspectRatio : desktopAspectRatio;
    }
    setDescription('');
  }, []);
  useEffect(() => {
    if (carousel && carousel[0] && carousel[0].description) {
      setDescription(carousel[0].description);
    } else {
      setDescription('');
    }
  }, [carousel]);
  const sliderSettings = {
    className: 'slider w-full h-full overflow-hidden',
    dots: false,
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    swipeToSlide: true,
    touchThreshold: 100,
    prevArrow: <CustomPrevArrow isMobile={isMobile} noArrows={noArrows} />,
    nextArrow: <CustomNextArrow isMobile={isMobile} noArrows={noArrows} />,
    afterChange: (current) => {
      setPosition(current);
      if (carousel && carousel[current] && carousel[current].description) {
        setDescription(carousel[current].description);
      } else {
        setDescription('');
      }
    },
  };
  return (
    <>
      <div aria-hidden='true'>
        <span
          aria-label={
            position === 1
              ? 'Use tab to explore slide content'
              : 'Use tab to explore slide content'
          }
        ></span>
      </div>
      <Container
        className={cx(
          'w-full h-full relative flex flex-col items-center overflow-hidden',
          className,
          {
            rounded: isContainerRounded,
          },
        )}
        fullWidth={fullWidth}
        rounded={rounded}
        ref={containerRef}
      >
        <AspectRatio
          ratio={isMobile ? mobileAspectRatio : desktopAspectRatio}
          className='h-full w-full '
        >
          <Slider {...sliderSettings}>
            {carousel &&
              carousel.map((item, index) => {
                let imageSrc = item.src;
                if (item.image) {
                  imageSrc =
                    (isMobile ? item.image.srcMobile : item.image.srcDesktop) ||
                    item.image.src;
                }
                const hasMultiblockVideo =
                  (item.video && item.video.href) || false;
                const isVideo =
                  ['internal', 'external'].includes(item.type) ||
                  hasMultiblockVideo;
                const itemVideoType = getVideoType(
                  item.src || hasMultiblockVideo,
                );
                const videoTranscript = hasMultiblockVideo
                  ? item.transcriptFile
                  : {};
                const videoDescription = hasMultiblockVideo
                  ? item.transcriptText
                  : '';
                const autoPlay = item.autoPlay || false;
                return (
                  <>
                    <SlideContainer
                      ref={galleryContainerRef}
                      className={cx(
                        'h-full w-full justify-center text-center rounded flex',
                        {
                          'pb-8': hasMultiblockVideo && isMultiblockComponent,
                          // "mb-16": !hasMultiblockVideo && isMultiblockComponent,
                        },
                      )}
                      key={item.image ? item.image.src : item.id}
                      fullWidth={fullWidth}
                      // withTopBar={withTopBar}
                    >
                      {isVideo && itemVideoType === 'external' && (
                        <IframeContainer
                          className={cx('videoIframe w-full h-full rounded', {
                            'px-8 py-8': isMobile && !hasMultiblockVideo,
                            'px-0': !isMobile && !hasMultiblockVideo,
                            'bg-light-blue': backgroundColor === 'light-blue',
                            'bg-black': backgroundColor === 'black',
                          })}
                        >
                          <Iframe
                            ref={(el) => (itemRefs.current[index] = el)}
                            tabIndex={
                              (position === 0 && index === 0) ||
                              activeIndex === index
                                ? '0'
                                : '-1'
                            }
                            aria-hidden={
                              (position === 0 && index === 0) ||
                              activeIndex === index
                                ? 'false'
                                : 'true'
                            }
                            className='rounded'
                            src={getEmbedUrl(
                              hasMultiblockVideo ? item.video.href : item.src,
                              autoPlay,
                              true,
                            )}
                            title={item.description}
                            aria-label={activeIndex + ' ' + index}
                            alt={item.description}
                          />
                        </IframeContainer>
                      )}
                      {isVideo && itemVideoType === 'internal' && (
                        <div className='w-full h-full rounded flex justify-center items-center'>
                          {hasMultiblockVideo ? (
                            <InternalVideo
                              ref={(el) => (itemRefs.current[index] = el)}
                              tabIndex={
                                (position === 0 && index === 0) ||
                                activeIndex === index
                                  ? '0'
                                  : '-1'
                              }
                              aria-hidden={
                                (position === 0 && index === 0) ||
                                activeIndex === index
                                  ? 'false'
                                  : 'true'
                              }
                              src={item.video.href}
                              className='min-h-full min-w-full bg-black rounded text-center'
                              controls
                              disablePictureInPicture='true'
                              controlsList='nodownload'
                              autoPlay={autoPlay}
                            />
                          ) : (
                            <InternalVideo
                              ref={(el) => (itemRefs.current[index] = el)}
                              tabIndex={
                                (position === 0 && index === 0) ||
                                activeIndex === index
                                  ? '0'
                                  : '-1'
                              }
                              aria-hidden={
                                (position === 0 && index === 0) ||
                                activeIndex === index
                                  ? 'false'
                                  : 'true'
                              }
                              src={item.src}
                              controls
                              disablePictureInPicture='true'
                              controlsList='nodownload'
                              className='block max-h-full rounded text-center'
                              autoPlay={autoPlay}
                            />
                          )}
                        </div>
                      )}
                      {!isVideo &&
                        (item.type === 'tour' ? (
                          <IframeContainer
                            aria-hidden={
                              (position === 0 && index === 0) ||
                              activeIndex === index
                                ? 'false'
                                : 'true'
                            }
                            tabIndex={
                              (position === 0 && index === 0) ||
                              activeIndex === index
                                ? '0'
                                : '-1'
                            }
                            className={cx('w-full h-full px-0 md:px-0 ', {
                              'bg-light-blue': backgroundColor === 'light-blue',
                            })}
                          >
                            <Iframe
                              ref={(el) => (itemRefs.current[index] = el)}
                              aria-hidden={
                                (position === 0 && index === 0) ||
                                activeIndex === index
                                  ? 'false'
                                  : 'true'
                              }
                              tabIndex={
                                (position === 0 && index === 0) ||
                                activeIndex === index
                                  ? '0'
                                  : '-1'
                              }
                              src={getScrFromIframe(item.src)}
                              title={item.description}
                            />
                          </IframeContainer>
                        ) : (
                          <>
                            {
                              <div
                                //role='button'
                                className={cx('flex rounded text-center', {
                                  'min-w-full min-h-full':
                                    !plansStyle && !communityGallery,
                                  'h-full w-full pt-6 pb-12 lg:pb-20 flex justify-center':
                                    plansStyle && isPlanType(item),
                                  'w-full': communityGallery,
                                })}
                              >
                                <ImgContainer
                                  role='button'
                                  ref={(el) => (itemRefs.current[index] = el)}
                                  tabIndex={
                                    (position === 0 && index === 0) ||
                                    activeIndex === index
                                      ? '0'
                                      : '-1'
                                  }
                                  aria-hidden={
                                    (position === 0 && index === 0) ||
                                    activeIndex === index
                                      ? 'false'
                                      : 'true'
                                  }
                                  src={imageSrc}
                                  alt={item.image ? item.image.alt : item.alt}
                                  fullWidth={fullWidth}
                                  isCondo={plansStyle && isPlanType(item)}
                                  className={cx('', {
                                    'rounded min-w-full object-contain min-h-full':
                                      !plansStyle && !communityGallery,
                                    'md:rounded h-full w-full object-cover':
                                      communityGallery,
                                    'object-scale-down xl:pb-16 justify-self-center mb-4':
                                      plansStyle && isPlanType(item),
                                  })}
                                />
                              </div>
                            }
                            {hasZoomIcons && !isMobile && (
                              <>
                                <button
                                  aria-label='Zoom in'
                                  className={cx(
                                    'absolute right-0 top-0 mt-3 mr-4 h-8 w-8 bg-white rounded-full hover:text-white text-xl pb-1 text-bold z-10',
                                    {
                                      'text-action-blue hover:bg-action-blue':
                                        !showNewSchemaColor,
                                      'text-black hover:bg-black':
                                        showNewSchemaColor,
                                    },
                                  )}
                                  onClick={() => {
                                    // zoom in
                                    let myImg =
                                      itemRefs &&
                                      itemRefs.current &&
                                      itemRefs.current[index];
                                    let currHeight =
                                      myImg && myImg.clientHeight;
                                    myImg.classList.remove('h-full');
                                    myImg.classList.remove('min-w-full');

                                    let currParent = myImg.parentNode;
                                    if (currParent) {
                                      currParent.style.textAlign =
                                        '-webkit-center';
                                    }
                                    if (currHeight > 1000) return false;
                                    else {
                                      myImg.style.height =
                                        currHeight + 50 + 'px';
                                    }
                                    myImg.style.width = 'auto';
                                  }}
                                >
                                  +
                                </button>
                                <button
                                  aria-label='Zoom out'
                                  className={cx(
                                    'absolute right-0 top-0 mt-12 mr-4 h-8 w-8 bg-white rounded-full hover:text-white text-xl pb-1 text-bold z-10',
                                    {
                                      'text-action-blue hover:bg-action-blue':
                                        !showNewSchemaColor,
                                      'text-black hover:bg-black':
                                        showNewSchemaColor,
                                    },
                                  )}
                                  onClick={() => {
                                    // zoom out
                                    let myImg =
                                      itemRefs &&
                                      itemRefs.current &&
                                      itemRefs.current[index];
                                    let currHeight =
                                      myImg && myImg.clientHeight;
                                    myImg.classList.remove('h-full');
                                    myImg.classList.remove('min-w-full');

                                    let currParent = myImg.parentNode;
                                    if (currParent) {
                                      currParent.style.textAlign =
                                        '-webkit-center';
                                    }

                                    if (currHeight < 300) return false;
                                    else {
                                      myImg.style.height =
                                        currHeight - 50 + 'px';
                                      myImg.style.width = 'auto';
                                    }
                                  }}
                                >
                                  -
                                </button>
                              </>
                            )}
                            {messageFlag && !isMobile && (
                              <div className='absolute top-0 left-0 flex flex-row w-full mt-4 z-0'>
                                <QMIFlagMessageContainer className='flex items-center justify-center px-6 py-1 mt-10-mattamy md:mt-1 text-promo-dark-blue border-promo-dark-blue bg-aqua rounded-r'>
                                  <p className='px-4 text-center font-trade-gothic-20 uppercase text-lg tracking-wide leading-3 mt-1'>
                                    {messageFlag}
                                  </p>
                                </QMIFlagMessageContainer>
                              </div>
                            )}
                          </>
                        ))}
                    </SlideContainer>

                    {hasMultiblockVideo &&
                      videoTranscript &&
                      videoTranscript.link &&
                      videoTranscript.link.href &&
                      videoDescription && (
                        <div
                          className={cx('absolute bottom-0 left-0', {
                            'lg:ml-16': !isRightAligned,
                          })}
                        >
                          <span
                            className={cx(
                              'richtext text-left text-md leading-7 font-graphie mt-2',
                              {
                                'lg:ml-4 text-mattamy-blue lg:text-white':
                                  !isRightAligned,
                                'text-white': isRightAligned,
                              },
                            )}
                          >
                            {'Transcript: '}
                            {
                              <a
                                href={videoTranscriptFileUrl(videoTranscript)}
                                download={
                                  videoTranscript.name +
                                  videoTranscriptFileExtension(
                                    videoTranscript.extension,
                                  )
                                }
                                className='underline'
                                aria-label={
                                  'Download ' + videoDescription + ' transcript'
                                }
                              >
                                {videoDescription}
                              </a>
                            }
                          </span>
                        </div>
                      )}
                  </>
                );
              })}
          </Slider>
        </AspectRatio>
        {fullWidth && description && (
          <DescriptionSpan
            aria-live='polite'
            className={cx(
              'rounded-full bg-white absolute inset-x-0 bottom-0 px-6 md:px-4 text-center text-sm md:text-lg font-graphie font-bold tracking-normal  leading-6 md:leading-relaxed',
              {
                /* "text-white":
                            !withTopBar || descriptionColor === "white",
                          "text-mattamy-blue": !showNewSchemaColor && withTopBar,
                          "text-black": showNewSchemaColor && withTopBar,*/
                'text-mattamy-blue': !showNewSchemaColor,
                'text-black': showNewSchemaColor,
                'mb-10 py-2 md:py-6': !counterRight,
                'md:mb-5 mb-12 md:mb-0': counterRight,
              },
            )}
          >
            {description}
          </DescriptionSpan>
        )}
        {(carousel.length > 1 || alwaysShowLen) && (
          <div
            className={cx(
              'absolute bottom-0 rounded-full text-sm px-3 flex justify-center items-center font-trade-gothic-20',
              {
                'bg-mattamy-blue text-white': withTopBar,
                'bg-white text-mattamy-blue': !withTopBar,
                'mb-4': !counterRight && !isMultiblockComponent,
                'mb-12': !counterRight && isMultiblockComponent,
                'mb-5 right-0 mr-5': counterRight,
              },
            )}
          >
            <p aria-live='polite' className='mt-1'>
              <span
                style={{
                  position: 'absolute',
                  left: '-10000px',
                  top: 'auto',
                  width: '1px',
                  height: '1px',
                  overflow: 'hidden',
                }}
              >
                Slide number{' '}
              </span>
              {Math.sign(position) !== -1
                ? (position % len) + 1
                : ((len - Math.abs(position % len)) % len) + 1}
              /{len}
            </p>
          </div>
        )}
      </Container>
    </>
  );
};

ImageCarousel.propTypes = {
  carousel: PropTypes.array.isRequired,
  className: PropTypes.string,
  onChange: PropTypes.func,
  fullWidth: PropTypes.bool,
  handleImageClick: PropTypes.func,
};

export default ImageCarousel;
